import React from 'react';
import { Paper, Typography, Box, Link, Divider, Tooltip } from '@mui/material';

/**
 * ── Amazon Associates configuration ──────────────────────────────────────
 * Replace with your own Associates Partner/Store tag (e.g. "lifan-20").
 * This is the ONLY value needed for static affiliate links — it is public by
 * design (it appears in the outbound URL) and is NOT your Creators API secret.
 * Never put the Creators API Credential Secret in this client-side app.
 */
const PARTNER_TAG = 'amazonprimepromo-20';

/** Amazon marketplace host used for affiliate links. */
const AMAZON_HOST = 'https://www.amazon.com';

interface Recommendation {
  label: string;
  blurb: string;
  /** Search keywords; using a search link keeps the link valid over time. */
  keywords: string;
  emoji: string;
}

// Curated for the document / PDF / productivity audience, grouped by
// category so different pages (e.g. guide articles) can surface picks
// relevant to what they're about instead of always showing the same list.
const RECOMMENDATIONS_BY_CATEGORY: Record<string, Recommendation[]> = {
  general: [
    { label: 'Document Scanners', blurb: 'Digitize paper into PDFs', keywords: 'document scanner', emoji: '🖨️' },
    { label: 'Kindle Scribe', blurb: 'Read & annotate PDFs', keywords: 'kindle scribe', emoji: '📖' },
    { label: 'Portable SSDs', blurb: 'Store your converted files', keywords: 'portable ssd', emoji: '💾' },
    { label: 'Productivity Books', blurb: 'Get more done', keywords: 'productivity books bestsellers', emoji: '📚' },
    { label: 'Laptop Stands', blurb: 'Work ergonomically', keywords: 'laptop stand', emoji: '💻' },
    { label: 'Blue-Light Glasses', blurb: 'Easier on the eyes', keywords: 'blue light blocking glasses', emoji: '👓' },
  ],
  scanning: [
    { label: 'Document Scanners', blurb: 'Digitize paper into searchable PDFs', keywords: 'document scanner', emoji: '🖨️' },
    { label: 'Sheet-Fed Scanners', blurb: 'Fast batch scanning', keywords: 'sheet fed document scanner', emoji: '📠' },
    { label: 'Portable SSDs', blurb: 'Store your scanned archive', keywords: 'portable ssd', emoji: '💾' },
  ],
  writing: [
    { label: 'Mechanical Keyboards', blurb: 'Nicer to type on all day', keywords: 'mechanical keyboard quiet', emoji: '⌨️' },
    { label: 'Ergonomic Mice', blurb: 'Easier on your wrist', keywords: 'ergonomic vertical mouse', emoji: '🖱️' },
    { label: 'Noise-Cancelling Headphones', blurb: 'Focus while you write', keywords: 'noise cancelling headphones', emoji: '🎧' },
    { label: 'Writing Style Guides', blurb: 'Sharpen your prose', keywords: 'writing style guide book', emoji: '✍️' },
  ],
  typing: [
    { label: 'Mechanical Keyboards', blurb: 'Crisp, consistent key feel', keywords: 'mechanical keyboard', emoji: '⌨️' },
    { label: 'Keycap Sets', blurb: 'Refresh your keyboard', keywords: 'pbt keycap set', emoji: '🔤' },
    { label: 'Wrist Rests', blurb: 'Comfort for long sessions', keywords: 'keyboard wrist rest', emoji: '🤲' },
    { label: 'Desk Mats', blurb: 'Smooth, quiet desk surface', keywords: 'desk mat large', emoji: '🟫' },
  ],
  formats: [
    { label: 'Productivity Books', blurb: 'Get more done', keywords: 'productivity books bestsellers', emoji: '📚' },
    { label: 'Portable SSDs', blurb: 'Store your converted files', keywords: 'portable ssd', emoji: '💾' },
    { label: 'Laptop Stands', blurb: 'Work ergonomically', keywords: 'laptop stand', emoji: '💻' },
  ],
};

const buildAffiliateUrl = (keywords: string): string => {
  const params = new URLSearchParams({ k: keywords, tag: PARTNER_TAG });
  return `${AMAZON_HOST}/s?${params.toString()}`;
};

interface AffiliateRecommendationsProps {
  /** Picks a curated set from RECOMMENDATIONS_BY_CATEGORY; falls back to 'general'. */
  category?: string;
  /** Smaller, quieter styling for sidebars where the list shouldn't compete with the tool. */
  dense?: boolean;
  /** Heading for the dense variant. */
  title?: string;
}

const AffiliateRecommendations: React.FC<AffiliateRecommendationsProps> = ({ category, dense = false, title = 'Writing desk picks' }) => {
  const recommendations =
    (category && RECOMMENDATIONS_BY_CATEGORY[category]) || RECOMMENDATIONS_BY_CATEGORY.general;

  if (dense) {
    return (
      <Box component="aside" aria-label="Sponsored links" sx={{ px: 1, opacity: 0.85, '&:hover': { opacity: 1 } }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
          {title}
        </Typography>
        {recommendations.map((rec) => (
          <Link
            key={rec.label}
            href={buildAffiliateUrl(rec.keywords)}
            target="_blank"
            rel="sponsored noopener noreferrer"
            underline="hover"
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}
          >
            <Box component="span" aria-hidden>{rec.emoji}</Box>
            {rec.label}
          </Link>
        ))}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75, fontSize: '0.68rem' }}>
          As an Amazon Associate, the site earns from qualifying purchases.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
        🛍️ Recommended Tools
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Handpicked for document &amp; productivity workflows.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: '1fr' },
          gap: 1,
        }}
      >
        {recommendations.map((rec) => (
          <Tooltip key={rec.label} title={`Search Amazon for ${rec.label}`} placement="right">
            <Link
              href={buildAffiliateUrl(rec.keywords)}
              target="_blank"
              rel="sponsored noopener noreferrer"
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                p: 1,
                borderRadius: 1,
                color: 'text.primary',
                transition: 'background-color 0.2s ease',
                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.06)' },
              }}
            >
              <Box component="span" sx={{ fontSize: '1.25rem', lineHeight: 1 }}>
                {rec.emoji}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }} noWrap>
                  {rec.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {rec.blurb}
                </Typography>
              </Box>
            </Link>
          </Tooltip>
        ))}
      </Box>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
        As an Amazon Associate, the site earns from qualifying purchases. Links open Amazon in a new
        tab; no personal data is shared.
      </Typography>
    </Paper>
  );
};

export default AffiliateRecommendations;
